/* eslint-disable consistent-default-export-name/default-export-match-filename -- The wrapper preserves the upstream component name. */
import type { ExperienceSocialConnector } from '@logto/schemas';
import Original from 'monoai:original?containers/SocialSignInList/index';
import { useEffect, useMemo, useState } from 'react';

import { selectStudioProvider } from '@/apis/experience/studio-choice';
import socialButtonStyles from '@/components/Button/SocialLinkButton.module.scss';
import LastUsedTag from '@/components/LastUsedTag';
import buttonStyles from '@/shared/components/Button/index.module.scss';
import {
  beginSignInAttempt,
  hasSignInAttempt,
  lastSignInMethod,
} from '@/utils/last-sign-in-method';

import styles from './index.module.scss';
import studioStyles from './studio-options.module.scss';
import { studioProviderLogos } from './studio-provider-logos';
import useSocial from './use-social';
import { useStudioCountdown } from './use-studio-countdown';

type Props = {
  readonly className?: string;
  readonly socialConnectors?: ExperienceSocialConnector[];
};

type StudioProvider = 'google' | 'microsoft';

const providers: ReadonlyArray<{ id: StudioProvider; name: string }> = [
  { id: 'google', name: 'Google' },
  { id: 'microsoft', name: 'Microsoft' },
];

function lastStudioProvider(): StudioProvider | undefined {
  switch (lastSignInMethod()) {
    case 'studio:google': {
      return 'google';
    }
    case 'studio:microsoft': {
      return 'microsoft';
    }
    default: {
      return undefined;
    }
  }
}

type ButtonContentProps = {
  readonly logo: string;
  readonly alt: string;
  readonly label: string;
};

const ButtonContent = ({ logo, alt, label }: ButtonContentProps) => (
  <>
    {/* The brand stylesheet targets the Studio logo by its connector alt text. */}
    <img src={logo} alt={alt} aria-hidden="true" className={socialButtonStyles.icon} />
    <div className={socialButtonStyles.name}>
      <div className={socialButtonStyles.placeHolder} />
      <span className={studioStyles.label}>{label}</span>
      <div className={socialButtonStyles.placeHolder} />
    </div>
  </>
);

const StudioOptions = ({ connector }: { readonly connector: ExperienceSocialConnector }) => {
  const { invokeSocialSignIn } = useSocial();
  // Read storage once per page view so the tag and countdown don't shift mid-flow.
  // Returning to this tab after an unfinished attempt means it failed or was
  // abandoned; never start it again automatically.
  const { lastProvider, autoContinue } = useMemo(() => {
    const provider = lastStudioProvider();
    return {
      lastProvider: provider,
      autoContinue: provider !== undefined && !hasSignInAttempt(),
    };
  }, []);
  const [expanded, setExpanded] = useState(lastProvider !== undefined);
  const [busy, setBusy] = useState(false);
  async function signIn(provider: StudioProvider) {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      selectStudioProvider(provider);
      beginSignInAttempt(`studio:${provider}`);
      await invokeSocialSignIn(connector);
    } finally {
      setBusy(false);
    }
  }
  const countdown = useStudioCountdown(autoContinue && !busy, () => {
    if (lastProvider) {
      void signIn(lastProvider);
    }
  });
  const lastProviderName = providers.find(({ id }) => id === lastProvider)?.name;
  const style = `${buttonStyles.button} ${buttonStyles.secondary} ${buttonStyles.large} ${socialButtonStyles.socialButton} ${styles.socialLinkButton} ${studioStyles.button}`;
  return (
    <div className={styles.socialLinkList}>
      <button
        type="button"
        className={style}
        aria-expanded={expanded}
        disabled={busy}
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <ButtonContent logo={connector.logo} alt={connector.target} label="Sign in with Studio" />
      </button>
      {expanded && (
        <div
          className={`${styles.socialLinkList} ${studioStyles.providers}`}
          role="group"
          aria-label="Studio sign-in providers"
        >
          {providers.map(({ id, name }) => (
            <button
              key={id}
              type="button"
              className={`${style} ${studioStyles.provider}`}
              disabled={busy}
              onClick={() => {
                void signIn(id);
              }}
            >
              <ButtonContent
                logo={studioProviderLogos[id]}
                alt={id}
                label={`Continue with ${name}`}
              />
              {id === lastProvider && <LastUsedTag />}
            </button>
          ))}
        </div>
      )}
      {countdown.remaining !== undefined && lastProviderName && (
        <div className={studioStyles.countdown}>
          <span role="status">
            Continuing with {lastProviderName} in {countdown.remaining}s
          </span>
          <button type="button" className={studioStyles.cancel} onClick={countdown.cancel}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

// eslint-disable-next-line react/function-component-definition -- Additive wrappers require function declarations.
export default function SocialSignInList(props: Props) {
  const [mode, setMode] = useState<'loading' | 'enabled' | 'legacy' | 'unavailable'>('loading');
  useEffect(() => {
    const abort = new AbortController();
    async function loadConfiguration() {
      try {
        const response = await fetch('/api/monoai/studio/configuration', { signal: abort.signal });
        const data: unknown = response.ok ? await response.json() : null;
        if (abort.signal.aborted) {
          return;
        }
        if (
          data &&
          typeof data === 'object' &&
          'enabled' in data &&
          typeof data.enabled === 'boolean'
        ) {
          setMode(data.enabled ? 'enabled' : 'legacy');
        } else {
          setMode('unavailable');
        }
      } catch {
        if (!abort.signal.aborted) {
          setMode('unavailable');
        }
      }
    }
    void loadConfiguration();
    return () => {
      abort.abort();
    };
  }, []);
  const studio = props.socialConnectors?.find(({ target }) => target === 'core');
  if (mode === 'legacy' || !studio) {
    return <Original {...props} />;
  }
  // An empty original list still renders its spaced container, which would
  // double the gap below the divider.
  const others = props.socialConnectors?.filter(({ target }) => target !== 'core') ?? [];
  return (
    <>
      {others.length > 0 && <Original {...props} socialConnectors={others} />}
      {mode === 'enabled' ? (
        <StudioOptions connector={studio} />
      ) : (
        <button
          disabled
          className={`${buttonStyles.button} ${buttonStyles.secondary} ${buttonStyles.large}`}
          type="button"
        >
          {mode === 'loading' ? 'Sign in with Studio' : 'Studio sign-in unavailable'}
        </button>
      )}
    </>
  );
}
/* eslint-enable consistent-default-export-name/default-export-match-filename */
